/**
 * WaitingApprovalScreen — the approval gate.
 *
 * Shown while `onboardingStatus === 'WAITING_APPROVAL'`. Polls
 * `GET /auth/check-status`, which is one of the three routes the backend marks
 * `@AllowsPendingOnboarding()` precisely so this screen can exist. The moment
 * a platform admin approves the merchant in web-admin, the poll picks it up and
 * the gate advances to store setup on its own.
 *
 * Ports BigxBookApp/src/screens/setup/WaitingApprovalScreen.tsx, plus one
 * browser-specific behaviour: a background tab is re-checked as soon as it is
 * focused. A merchant who leaves this open in a tab overnight and comes back in
 * the morning should not wait up to another 30 seconds to be told they were
 * approved hours ago.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { checkStatus, terminalStatusFromError } from '@/auth/onboardingApi';
import { OnboardingCard, PRIMARY, QUIET } from './OnboardingCard';

const POLL_INTERVAL_MS = 30_000;

export function WaitingApprovalScreen({ onLogout }: { onLogout: () => void }) {
  const currentUser = useAppStore((s) => s.currentUser);
  const setCurrentUser = useAppStore((s) => s.setCurrentUser);

  const [checking, setChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  // The poll reads currentUser to compare statuses; holding it in a ref keeps
  // that out of the effect's dependencies, so approving does not tear down and
  // restart the interval mid-flight.
  const userRef = useRef(currentUser);
  userRef.current = currentUser;

  const poll = useCallback(async () => {
    const user = userRef.current;
    if (!user) return;

    setChecking(true);
    try {
      const res = await checkStatus();
      const next = res.user?.onboardingStatus;
      if (next && next !== user.onboardingStatus) {
        // Any change — APPROVED or REJECTED — is handled by writing the status
        // and letting the gate re-route. This screen does not navigate itself.
        await setCurrentUser({
          ...user,
          name: res.user?.name ?? user.name,
          onboardingStatus: next,
        });
      }
      setLastChecked(new Date());
    } catch (err) {
      // A refusal does not come back as a status — it comes back as a 401 from
      // JwtStrategy, which blocks REJECTED and SUSPENDED merchants before any
      // controller runs. Catching it here is what stops a refused merchant
      // waiting on this screen forever. See terminalStatusFromError().
      const terminal = terminalStatusFromError(err);
      if (terminal) {
        await setCurrentUser({ ...user, onboardingStatus: terminal });
        return;
      }
      // Otherwise deliberately silent: this runs unattended on a loop, and a
      // transient network blip is not news. The "last checked" timestamp going
      // stale is the signal that something is wrong.
    } finally {
      setChecking(false);
    }
  }, [setCurrentUser]);

  useEffect(() => {
    void poll();
    const id = setInterval(() => void poll(), POLL_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') void poll();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [poll]);

  return (
    <OnboardingCard
      title="Registration under review"
      subtitle="Your business has been submitted. Our team usually reviews within a few hours."
      footer={
        <button type="button" onClick={onLogout} className={`${QUIET} w-full`}>
          Sign out
        </button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-white/10 bg-white/[0.06] p-4 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <span
              className="inline-block h-2 w-2 shrink-0 animate-pulse rounded-full bg-accent"
              aria-hidden
            />
            <p className="text-body text-fg-primary">
              This page updates by itself — you can leave it open.
            </p>
          </div>
          <p className="mt-2 text-label text-fg-tertiary">
            {lastChecked
              ? `Last checked at ${lastChecked.toLocaleTimeString()}`
              : 'Checking…'}
          </p>
        </div>

        {currentUser?.phone && (
          <p className="text-label text-fg-tertiary">
            Registered to {currentUser.phone}. Approval applies to this number.
          </p>
        )}

        <button
          type="button"
          onClick={() => void poll()}
          disabled={checking}
          className={PRIMARY}
        >
          {checking ? 'Checking…' : 'Check now'}
        </button>
      </div>
    </OnboardingCard>
  );
}
